import { useState } from "react";
import { ButtonAdd } from "@/components/ui/button";
import { formatPrice } from "@/lib/utils";
import { useModal, usePagination, useRestaurantCurrency } from "@/lib/hooks";
import { useAppSelector } from "@/redux/features/hooks";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useGetMenuFormulasQuery,
  useUpdateMenuFormulaMutation,
} from "@/redux/services/menu/menu_formula.api";
import { MenuFormula } from "@/redux/services/menu/menu_formula.type";
import FormulaFormDrawer from "@/components/menu/formula/FormulaFormDrawer";
import FormulaDetailsDrawer from "@/components/menu/formula/FormulaDetailsDrawer";
import { Clock, Calendar, Layers } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import Switch from "@/components/ui/switch";
import ReactPagination from "@/components/Table/Pagination";
import toast from "react-hot-toast";

const DAY_LABELS: Record<number, string> = {
  0: "Dim",
  1: "Lun",
  2: "Mar",
  3: "Mer",
  4: "Jeu",
  5: "Ven",
  6: "Sam",
};

function formatTimeShort(time?: string | null): string {
  if (!time) return "";
  if (time.includes("T")) {
    return time.split("T")[1]?.substring(0, 5) ?? "";
  }
  return time.substring(0, 5);
}

const TabsFormula = () => {
  const { limit, onChange, page } = usePagination(12);
  const { currentRestaurant } = useAppSelector((state) => state.user);
  const { canManage } = usePermissions();
  const canManageMenu = canManage("menu");
  const currency = useRestaurantCurrency();

  const { data, isLoading } = useGetMenuFormulasQuery(
    { restaurantId: currentRestaurant?.id, query: { limit, page } },
    { skip: !currentRestaurant?.id }
  );

  const [updateFormula] = useUpdateMenuFormulaMutation();
  const [loadingFormulas, setLoadingFormulas] = useState<
    Record<string, boolean>
  >({});

  const {
    isOpen: isFormOpen,
    item: selectedFormula,
    openModal: openCreateForm,
    openEditModal: openEditForm,
    closeModal: closeForm,
  } = useModal<MenuFormula>();

  const [detailsFormulaId, setDetailsFormulaId] = useState<string | null>(null);

  const handleEditFromDetails = (formula: MenuFormula) => {
    setDetailsFormulaId(null);
    openEditForm(formula);
  };

  const handleToggleActive = async (formula: MenuFormula) => {
    setLoadingFormulas((prev) => ({ ...prev, [formula.id]: true }));
    try {
      const newIsActive = !formula.isActive;
      await updateFormula({
        id: formula.id,
        data: {
          isActive: newIsActive,
          ...(newIsActive && formula.daysOfWeek.length === 0
            ? { daysOfWeek: [0, 1, 2, 3, 4, 5, 6] }
            : {}),
        },
      }).unwrap();
      toast.success(
        `Formule ${newIsActive ? "activée" : "désactivée"} avec succès`
      );
    } catch {
      toast.error("Une erreur est survenue");
    } finally {
      setLoadingFormulas((prev) => ({ ...prev, [formula.id]: false }));
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-end">
          <Skeleton className="w-44 h-9" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      {canManageMenu && (
        <div className="flex justify-end mb-6">
          <ButtonAdd
            label="Créer une formule"
            onClick={() => openCreateForm()}
          />
        </div>
      )}

      {data?.results && data.results.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {data.results.map((formula) => (
            <FormulaCard
              key={formula.id}
              formula={formula}
              currency={currency}
              isToggling={!!loadingFormulas[formula.id]}
              readOnly={!canManageMenu}
              onView={() => setDetailsFormulaId(formula.id)}
              onToggleActive={() => handleToggleActive(formula)}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center py-12">
          <Layers className="h-12 w-12 text-muted-foreground opacity-30 mb-3" />
          <p className="text-muted-foreground text-lg mb-2">
            Aucune formule
          </p>
          <p className="text-muted-foreground text-sm">
            Cliquez sur "Créer une formule" pour commencer
          </p>
        </div>
      )}

      {data?.results &&
        data.results.length > 0 &&
        data?.count &&
        (data.count > limit || limit > 12) && (
          <div className="flex-shrink-0 mt-10">
            <ReactPagination
              itemsPerPage={limit}
              count={data.count}
              onChange={onChange}
              currentPage={page}
              options={[
                { value: 12, label: "12" },
                { value: 24, label: "24" },
              ]}
            />
          </div>
        )}

      <FormulaDetailsDrawer
        formulaId={detailsFormulaId}
        isOpen={!!detailsFormulaId}
        onClose={() => setDetailsFormulaId(null)}
        onEdit={handleEditFromDetails}
      />

      <FormulaFormDrawer
        isOpen={isFormOpen}
        onClose={closeForm}
        formula={selectedFormula}
      />
    </>
  );
};

// ---------- FormulaCard ----------

interface FormulaCardProps {
  formula: MenuFormula;
  currency: string;
  isToggling: boolean;
  readOnly?: boolean;
  onView: () => void;
  onToggleActive: () => void;
}

function FormulaCard({
  formula,
  currency,
  isToggling,
  readOnly,
  onView,
  onToggleActive,
}: FormulaCardProps) {
  const startTime = formatTimeShort(formula.startTime);
  const endTime = formatTimeShort(formula.endTime);
  const timeLabel =
    startTime && endTime ? `${startTime} - ${endTime}` : "Toute la journée";

  const componentCount = formula.components?.length ?? 0;

  return (
    <div className="bg-card rounded-lg border border-border shadow-sm transition-colors hover:border-primary hover:bg-muted/40 flex flex-col">
      <button
        type="button"
        onClick={onView}
        className="p-4 flex-1 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-t-lg"
      >
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-card-foreground text-base">
              {formula.name}
            </h3>
            {formula.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {formula.description}
              </p>
            )}
          </div>
          <div className="flex flex-col items-end flex-shrink-0 ml-2">
            <span className="text-primary font-semibold">
              {formatPrice(formula.price / 100, currency)}
            </span>
            <span
              className={`mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                formula.isActive
                  ? "bg-accent-light text-accent"
                  : "bg-muted text-muted-foreground"
              }`}
            >
              {formula.isActive ? "Active" : "Inactive"}
            </span>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-muted-foreground mb-3">
          <span className="flex items-center gap-1.5">
            <Layers className="h-3.5 w-3.5" />
            {componentCount} composant{componentCount !== 1 ? "s" : ""}
          </span>
        </div>

        {/* Schedule */}
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            <div className="flex gap-0.5">
              {[...(formula.daysOfWeek ?? [])]
                .sort((a, b) => a - b)
                .map((day) => (
                  <span
                    key={day}
                    className="px-1.5 py-0.5 rounded bg-primary/10 text-primary text-[10px] font-medium"
                  >
                    {DAY_LABELS[day]}
                  </span>
                ))}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            <span>{timeLabel}</span>
          </div>
        </div>
      </button>

      {!readOnly && (
        <div
          className="px-4 py-3 border-t border-border flex items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            id={`formula-status-${formula.id}`}
            checked={formula.isActive}
            onChange={onToggleActive}
            disabled={isToggling}
          />
          <span className="text-xs text-muted-foreground">
            {isToggling
              ? "Mise à jour..."
              : formula.isActive
              ? "Active"
              : "Inactive"}
          </span>
        </div>
      )}
    </div>
  );
}

export default TabsFormula;
